import {
    AdvertisingCreativeItem,
    CreativeReportItemUpsertInput
} from './data/ReportItemDefinitionTypes';

const filterThatDontNeedTransformation = [
    'advertiserName',
    'advertiserDomain',
    'aiAdvertiserV1',
    'aiIndustryV1',
    'aiCategoryV1',
    'aiProductV1',
    'aiAdTypeV1',
    'aiFuelTypeV1'
];

/**
 * Report API stores fuel type as `fuelType`. Client state / explore filters use
 * `aiFuelTypeV1`. Call this only when sending an AdvertisingCreative definition
 * to report create/update.
 */
export function mapCreativeDefinitionToReportApi(
    definition: CreativeReportItemUpsertInput
): CreativeReportItemUpsertInput {
    const {aiFuelTypeV1, ...filterRest} = definition.filter;
    return {
        ...definition,
        columns: definition.columns.map((column) =>
            column === 'aiFuelTypeV1' ? 'fuelType' : column
        ),
        filter: {
            ...filterRest,
            ...(aiFuelTypeV1?.length ? {fuelType: aiFuelTypeV1} : {})
        }
    };
}

export default function convertCreativeDefinitionToFilter(
    input?: AdvertisingCreativeItem['definitionV2']['filter'],
    mapDateRangeToApiInput = true
) {
    if (!input) return {};

    const cleanFilter: CreativeReportItemUpsertInput['filter'] & {
        creativeDateRange?: CreativeReportItemUpsertInput['filter']['dateRangeFilter'];
    } = {
        ...(input.searchQuery && {searchQuery: input.searchQuery}),
        ...(input.status && {status: input.status}),
        ...(input.dateRangeFilter && {
            [mapDateRangeToApiInput ? 'dateRangeFilter' : 'creativeDateRange']:
                input.dateRangeFilter
        })
    };

    for (const key in input) {
        // Report API returns fuelType; always expose as aiFuelTypeV1 to the client.
        if (key === 'fuelType') {
            continue;
        }

        if (filterThatDontNeedTransformation.includes(key)) {
            cleanFilter[key] = input[key];
            continue;
        }

        if (
            // check if the original filter has a value
            input[key] &&
            input[key].length > 0 &&
            // check if the key is an entity
            [
                'adType',
                'campaign',
                'industry',
                'category',
                'mediaType',
                'product',
                'publication',
                'publisher',
                'mediaOwner',
                'region',
                'brand',
                'segment'
            ].includes(key)
        ) {
            cleanFilter[key] = input[key].map((ee) => ee.id);

            // if there are media type and placement filters in the definition, put the placement
            // filter immediately after the media type filter. doing this because placementType is
            // directly related to the mediaType, so they should be close to one another
            if (key === 'mediaType' && input.placementType && input.placementType.length > 0) {
                cleanFilter.placementType = input.placementType;
            }
        }
    }

    if (input.fuelType?.length) {
        cleanFilter.aiFuelTypeV1 = input.fuelType;
    }

    return cleanFilter;
}
